import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGS } from '@/i18n';
import { currentLang } from '@/hooks/useLocalized';
import { useToastStore } from '@/store/toast';
import type { Lang } from '@/types';
import { cn } from '@/lib/cn';

const LABEL: Record<Lang, string> = { uz: "O'zbek", ru: 'Русский', en: 'English' };
const SHORT: Record<Lang, string> = { uz: 'UZ', ru: 'RU', en: 'EN' };

export function LangSwitch() {
  const { t, i18n } = useTranslation();
  const push = useToastStore((s) => s.push);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = currentLang(i18n.language);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const change = (lng: Lang) => {
    void i18n.changeLanguage(lng);
    setOpen(false);
    push(t('toast.langChanged'), 'info');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn btn-ghost gap-1.5 rounded-DEFAULT px-2.5 py-2"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('lang.label')}
      >
        <Globe className="h-4 w-4" />
        <span className="text-sm font-medium">{SHORT[active]}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-40 overflow-hidden rounded-lg border border-line bg-elevated py-1 shadow-lg animate-fade-in-fast"
          role="listbox"
        >
          {SUPPORTED_LANGS.map((lng) => (
            <button
              key={lng}
              role="option"
              aria-selected={active === lng}
              onClick={() => change(lng)}
              className={cn(
                'flex w-full items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-surface',
                active === lng ? 'font-semibold text-content' : 'text-content-secondary',
              )}
            >
              {LABEL[lng]}
              {active === lng && <Check className="h-4 w-4 text-brand-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
